import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { getQuestions, getLatestCheckin, submitCheckin } from '../../services/quarterlyCheckin.service';

const SECTOR_LABELS = {
  financial: 'Financial', physical: 'Physical', mental: 'Mental',
  emotional: 'Emotional', career: 'Career', spiritual: 'Spiritual',
};

// "2026-Q3" -> "Q3" — each submission is its own sequential quarter (see
// nextQuarterLabel on the server), not tied to the calendar quarter.
const quarterDisplayLabel = (quarter) => quarter?.split('-Q')[1] ? `Q${quarter.split('-Q')[1]}` : quarter;

export default function QuarterlyCheckin() {
  const [sectors, setSectors] = useState(null);
  const [latest, setLatest] = useState(undefined); // undefined = not loaded yet, null = none exists
  const [mode, setMode] = useState('loading'); // loading | results | form
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(600);
  const iframeRef = useRef(null);

  useEffect(() => {
    Promise.all([getQuestions(), getLatestCheckin()]).then(([s, l]) => {
      setSectors(s);
      setLatest(l);
      // Jenn, 2026-10-08: the link (Hub tab and the reminder email's "Start my
      // Compass Check-In") must open a fresh check-in, never the last summary.
      // Past results stay one click away via "See your last results".
      setMode('form');
    }).catch(() => setError('Could not load the check-in.'));
  }, []);

  // Real original page's own markup/CSS/behavior, loaded isolated in an
  // iframe (client/public/quarterly-survey.html) — see that file's header
  // comment for why. This bridges it to the app: hands it the real
  // question data once it announces it's ready, resizes the iframe as its
  // content changes, and submits through our own real API (server-side
  // scoring, DB write, audit log, comparison-filed-to-member-record,
  // Unusual Activity flag) the moment it posts back a completed answer set.
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'quarterly-checkin-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'quarterly-checkin-init', sectors }, '*'
        );
      } else if (event.data.type === 'quarterly-checkin-height') {
        setIframeHeight(event.data.height);
      } else if (event.data.type === 'quarterly-checkin-submit') {
        handleSubmit(event.data.answers);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectors]);

  const handleSubmit = async (answers) => {
    setError(null);
    try {
      const checkin = await submitCheckin(answers);
      setJustSubmitted(checkin);
      setLatest(checkin);
      setMode('results');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your check-in.');
    }
  };

  const startOver = () => {
    setJustSubmitted(null);
    setMode('form');
  };

  if (mode === 'loading' || !sectors) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-sm text-gray-400">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <img src="/compass-logo-transparent.png" alt="" className="w-14 h-14 mx-auto mb-3" />
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Quarterly Compass Check-in</h1>
          <p className="text-sm text-gray-500 mt-1">
            A deeper check-in across all six sectors — takes about 5 minutes, once a quarter.
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {mode === 'results' && latest && (
          <ResultsView checkin={justSubmitted || latest} justSubmitted={!!justSubmitted} onRetake={startOver} />
        )}

        {mode === 'form' && latest && (
          <div className="mb-3 text-center">
            <button
              type="button"
              onClick={() => setMode('results')}
              className="text-xs text-gray-400 hover:text-reset-terracotta transition"
            >
              See your last results ({quarterDisplayLabel(latest.quarter)}) →
            </button>
          </div>
        )}

        {mode === 'form' && (
          <iframe
            ref={iframeRef}
            src="/quarterly-survey.html"
            title="Quarterly Compass Check-in"
            className="w-full border-0"
            style={{ height: iframeHeight }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

// Same visual language as the real results email (sendQuarterlyCheckinSnapshotEmail
// in email.js) — sage progress bars on a cream track, gold-topped cards for what's
// strong, terracotta-topped cards for what needs attention. Built to match what a
// member already receives in their inbox, not a separate look invented for the page.
function SectorBar({ label, percentage, tier }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-semibold text-reset-brown">{label}</span>
        <span className="text-sm">
          <span className="font-semibold text-reset-terracotta">{percentage}%</span>{' '}
          <span className="text-gray-400">{tier}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-reset-creamDark overflow-hidden">
        <div className="h-full bg-reset-sage rounded-full transition-all" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function HighlightCard({ label, borderColorClass, labelColorClass, sectorKey, score }) {
  return (
    <div className={`bg-reset-cream rounded-lg border-t-2 ${borderColorClass} px-5 py-4`}>
      <p className={`text-[11px] font-bold uppercase tracking-widest ${labelColorClass} mb-1.5`}>{label}</p>
      <p className="text-sm font-medium text-reset-brown">{SECTOR_LABELS[sectorKey]}</p>
      <p className="text-xs text-gray-400 mt-0.5">{score.percentage}% &mdash; {score.tier}</p>
    </div>
  );
}

function ResultsView({ checkin, justSubmitted, onRetake }) {
  return (
    <div className="space-y-5">
      {justSubmitted && (
        <div className="bg-reset-sage/10 border border-reset-sage/30 text-reset-brown rounded-xl p-4 text-sm">
          Thank you for checking in — your results are below, and we've emailed you a copy to keep.
        </div>
      )}

      {/* Overall score */}
      <div className="bg-reset-cream rounded-2xl border border-reset-creamDark p-8 text-center">
        <p className="text-[11px] font-bold uppercase tracking-widest text-reset-gold mb-1">{quarterDisplayLabel(checkin.quarter)} &middot; Your Overall Score</p>
        <p className="text-5xl font-bold text-reset-terracotta">
          {checkin.overall_percentage}<span className="text-2xl text-gray-400 font-normal">/100</span>
        </p>
        <p className="text-reset-brown/70 italic mt-1">{checkin.overall_tier}</p>
      </div>

      {/* Six sectors, sage bars on cream track — same treatment as the results email */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your six life sectors</p>
        {Object.keys(SECTOR_LABELS).map(key => (
          <SectorBar key={key} label={SECTOR_LABELS[key]} percentage={checkin.sector_scores[key].percentage} tier={checkin.sector_scores[key].tier} />
        ))}
      </div>

      {/* Highlight cards — gold for what's strong, terracotta for what needs attention */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <HighlightCard label="Your Strongest Sector" borderColorClass="border-reset-gold" labelColorClass="text-reset-gold" sectorKey={checkin.strongest_sector} score={checkin.sector_scores[checkin.strongest_sector]} />
        <HighlightCard label="Also Strong" borderColorClass="border-reset-gold" labelColorClass="text-reset-gold" sectorKey={checkin.second_sector} score={checkin.sector_scores[checkin.second_sector]} />
        <HighlightCard label="Your Growth Edge" borderColorClass="border-reset-terracotta" labelColorClass="text-reset-terracotta" sectorKey={checkin.growth_edge} score={checkin.sector_scores[checkin.growth_edge]} />
        <HighlightCard label="Worth Your Attention" borderColorClass="border-reset-terracotta" labelColorClass="text-reset-terracotta" sectorKey={checkin.attention_sector} score={checkin.sector_scores[checkin.attention_sector]} />
      </div>

      {/* Closing note — same sage-rule treatment as "What to do with this" in the email */}
      <div className="border-l-2 border-reset-sage pl-4 py-1">
        <p className="text-sm font-medium text-reset-sage mb-1">What to do with this</p>
        <p className="text-sm text-gray-500 leading-relaxed">Keep this somewhere easy to find. In 90 days, you'll do this check-in again — and you'll be able to see exactly how your picture has shifted.</p>
      </div>

      <button
        type="button"
        onClick={onRetake}
        className="text-xs text-gray-400 hover:text-reset-terracotta transition"
      >
        Do another check-in →
      </button>
    </div>
  );
}
